import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { RadioButtonModule } from 'primeng/radiobutton';
import { TransportFormService } from 'src/app/trip-calculator/services/transport-form.service';
import {
  CategoryItem,
  CategorySelectorComponent,
} from '../../../custom-input/category-selector/category-selector.component';
import { CalculationMode } from 'src/app/trip-calculator/models/calculation-mode.enum';
import {
  TransportCategory,
  TransportMode,
} from 'src/app/trip-calculator/models/transport';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-select-calculation-mode',
  imports: [
    ReactiveFormsModule,
    RadioButtonModule,
    FormsModule,
    CategorySelectorComponent,
    TranslocoPipe,
  ],
  templateUrl: './select-calculation-mode.component.html',
})
export class SelectCalculationModeComponent implements OnInit, OnDestroy {
  transportFormService = inject(TransportFormService);
  translocoService = inject(TranslocoService);
  categoryList: CategoryItem[] = [];
  subscription?: Subscription;

  ngOnInit() {
    this.initializeCategoryList();
    this.subscription = this.transportFormService.transportForm
      .get('customEmissionFactorId')
      ?.valueChanges.subscribe(() => {
        if (
          this.transportFormService.transportForm.get('type')?.value ===
          TransportMode.CustomFactor
        ) {
          this.initializeListForCustomFactor();
        }
      });
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }
  get calculationMode() {
    return this.transportFormService.calculationMode();
  }
  set calculationMode(value) {
    this.transportFormService.calculationMode.set(value as CalculationMode);
    this.transportFormService.destroyAllCalculationModeControls();
    if (
      value === CalculationMode.Duration ||
      value === CalculationMode.Distance
    ) {
      this.transportFormService.transportForm.get('return')?.setValue(false);
    }
  }

  initializeCategoryList() {
    if (
      this.transportFormService.transportForm.get('type')?.value ===
      TransportMode.CustomFactor
    ) {
      this.initializeListForCustomFactor();
    } else {
      this.categoryList.push(
        {
          icon: 'ruler-dimension-line',
          value: CalculationMode.Distance,
          label: this.translocoService.translate(
            'calculator.calculationMode.distance.title',
          ),

          description: this.translocoService.translate(
            'calculator.calculationMode.distance.description',
          ),
        },
        {
          icon: 'timer',
          value: CalculationMode.Duration,
          label: this.translocoService.translate(
            'calculator.calculationMode.duration.title',
          ),
          description: this.translocoService.translate(
            'calculator.calculationMode.duration.description',
          ),
        },
      );
      if (
        this.transportFormService.transportCategory() ===
        TransportCategory.Plane
      ) {
        this.categoryList.push(
          {
            icon: 'plane-takeoff',
            value: CalculationMode.Airport,
            label: this.translocoService.translate(
              'calculator.calculationMode.airport.title',
            ),
            description: this.translocoService.translate(
              'calculator.calculationMode.airport.description',
            ),
          },
          {
            icon: 'tickets-plane',
            value: CalculationMode.TIM,
            label: this.translocoService.translate(
              'calculator.calculationMode.tim.title',
            ),
            description: this.translocoService.translate(
              'calculator.calculationMode.tim.description',
            ),
            tooltipMessage: this.translocoService.translate(
              'timWarning.calculator',
            ),
          },
        );
      } else if (
        this.transportFormService.transportCategory() !== TransportCategory.Boat
      ) {
        this.categoryList.push({
          icon: 'map-pin',
          value: CalculationMode.Address,
          label: this.translocoService.translate(
            'calculator.calculationMode.address.title',
          ),
          description: this.translocoService.translate(
            'calculator.calculationMode.address.description',
          ),
        });
      }
    }
  }

  initializeListForCustomFactor() {
    this.categoryList = [];
    const factor = this.transportFormService.selectedCustomFactor;
    if (!!factor?.valuePerKm || (!!factor?.valuePerH && !!factor.speed)) {
      this.categoryList.push(
        {
          icon: 'ruler-dimension-line',
          value: CalculationMode.Distance,
          label: this.translocoService.translate(
            'calculator.calculationMode.distance.title',
          ),

          description: this.translocoService.translate(
            'calculator.calculationMode.distance.description',
          ),
        },
        {
          icon: 'map-pin',
          value: CalculationMode.Address,
          label: this.translocoService.translate(
            'calculator.calculationMode.address.title',
          ),
          description: this.translocoService.translate(
            'calculator.calculationMode.address.description',
          ),
        },
      );
    }
    if (!!factor?.valuePerH || (!!factor?.valuePerKm && !!factor?.speed)) {
      this.categoryList.push({
        icon: 'timer',
        value: CalculationMode.Duration,
        label: this.translocoService.translate(
          'calculator.calculationMode.duration.title',
        ),
        description: this.translocoService.translate(
          'calculator.calculationMode.duration.description',
        ),
      });
    }
  }
}
